import {useEffect, useState} from "react";
import {unzipSync} from "fflate";
import {api} from "@/shared/api";
import {ExportJobStatus} from "@/shared/timrTypes";
import type {ExportFile, LoadState, PdfDocument} from "./types";

const STATUS_POLL_INTERVAL = 1000;
const FALLBACK_FILE_NAME = "preview.pdf";

export interface ExportPreview {
	state: LoadState;
	file: ExportFile | null;
	documents: PdfDocument[];
}

function readDocuments(data: Uint8Array, fileName: string, contentType: string): PdfDocument[] {
	// startsWith, the content type carries a charset (spring.servlet.encoding.force)
	if (!contentType.startsWith("application/zip")) {
		return [{name: fileName, data}];
	}
	const entries = unzipSync(data, {filter: entry => entry.name.toLowerCase().endsWith(".pdf")});
	return Object.keys(entries)
		.sort((a, b) => a.localeCompare(b, undefined, {numeric: true}))
		.map(name => ({name, data: entries[name]}));
}

// prefers the UTF-8 encoded filename* (RFC 6266) over the ASCII fallback in filename
function readFileName(contentDisposition: string): string {
	const encoded = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition)?.[1];
	if (encoded) {
		return decodeURIComponent(encoded);
	}
	return /filename="?([^";]+)"?/.exec(contentDisposition)?.[1] ?? FALLBACK_FILE_NAME;
}

/**
 * Waits for an export job and downloads its result as the PDFs to preview. The cleanup only stops the work in the
 * browser (polling, download, object URL); the export job on the server is left alone.
 */
export function useExportPreview(exportJobId: string): ExportPreview {
	const [preview, setPreview] = useState<ExportPreview>({state: "loading", file: null, documents: []});

	useEffect(() => {
		let active = true;
		let request: JQuery.jqXHR | null = null;
		let pollTimeout: number | undefined;
		let fileUrl: string | null = null;

		const fail = (error: unknown) => {
			if (active) {
				console.error("Could not load PDF preview", error);
				setPreview({state: "error", file: null, documents: []});
			}
		};

		const showResult = async (blob: Blob, xhr: JQuery.jqXHR) => {
			const data = new Uint8Array(await blob.arrayBuffer());
			if (!active) {
				return;
			}
			const fileName = readFileName(xhr.getResponseHeader("Content-Disposition") ?? "");
			const documents = readDocuments(data, fileName, xhr.getResponseHeader("Content-Type") ?? blob.type);
			if (documents.length === 0) {
				throw new Error("Export contains no PDF");
			}
			fileUrl = URL.createObjectURL(blob);
			setPreview({state: "loaded", file: {url: fileUrl, fileName}, documents});
		};

		const download = () => {
			request = api(`export/download/${exportJobId}`)
				.options({xhrFields: {responseType: "blob"}})
				.get()
				.done((blob: Blob, _textStatus, xhr) => {
					showResult(blob, xhr).catch(fail);
				})
				.fail((_xhr, textStatus) => fail(new Error(`Export download failed: ${textStatus}`)));
		};

		const pollStatus = () => {
			request = api("export/status.json").get({exportJobId})
				.done((data: {status: ExportJobStatus}) => {
					if (!active) {
						return;
					}
					if (data.status === ExportJobStatus.Running || data.status === ExportJobStatus.Waiting) {
						pollTimeout = window.setTimeout(pollStatus, STATUS_POLL_INTERVAL);
					} else if (data.status === ExportJobStatus.Success) {
						download();
					} else {
						fail(new Error(`Export job ended with status ${data.status}`));
					}
				})
				.fail((_xhr, textStatus) => fail(new Error(`Export status request failed: ${textStatus}`)));
		};
		pollStatus();

		return () => {
			active = false;
			window.clearTimeout(pollTimeout);
			request?.abort();
			if (fileUrl) {
				URL.revokeObjectURL(fileUrl);
			}
		};
	}, [exportJobId]);

	return preview;
}
